// ============================================================================
// HOOKS/USE-USER-PROFILE — Fresh Firestore read of /users/{uid}
//
// Returns { loading, error, profile } where profile is the raw Firestore doc.
// Always performs a fresh getDoc — never uses cached AuthContext state.
// Errors are surfaced, never swallowed.
//
// Used by AnalyticsGuard (Stage 9) to enforce profile invariants before
// any analytics Firestore reads are attempted.
// ============================================================================

import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

function getDb() {
  return getFirestore(getApp());
}

/**
 * Reads /users/{uid} fresh from Firestore.
 *
 * @param {string|null|undefined} uid — Firebase Auth uid
 * @returns {{ loading: boolean, error: Error|null, profile: object|null }}
 *
 * Invariants:
 *   - Never swallows errors: getDoc failures are surfaced via `error`
 *   - Returns { error: Error } if uid is falsy (not authenticated)
 *   - Returns { error: Error } if doc does not exist
 *   - Cancels in-flight read if uid changes or component unmounts
 */
export function useUserProfile(uid) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setError(new Error('No authenticated user'));
      setProfile(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setProfile(null);

    getDoc(doc(getDb(), 'users', uid))
      .then(snap => {
        if (cancelled) return;
        if (!snap.exists()) {
          setError(new Error('User profile document not found'));
          setProfile(null);
        } else {
          setProfile({ uid, ...snap.data() });
          setError(null);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
        setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [uid]);

  return { loading, error, profile };
}
